import React, { useState } from 'react';
import { Button, Stack, FormControl } from 'react-bootstrap';
import fileDownload from 'js-file-download';
import { FaCog } from 'react-icons/fa';
import api from 'api';

type FilterChange = {
  search: string | null;
};

interface BurnCenterFilterProps {
  initialValue?: string | null;
  debounceMs?: number;
  onChange: (next: FilterChange) => void;
}

const toNull = (s: string) => (s.trim() === '' ? null : s.trim());

const BurnCenterListFilter: React.FC<BurnCenterFilterProps> = ({
  onChange,
  initialValue = '',
  debounceMs = 400,
}) => {
  const [searchValue, setSearchValue] = useState(initialValue ?? '');
  const [downloading, setDownloading] = React.useState(false);

  React.useEffect(() => {
    setSearchValue(initialValue ?? '');
  }, [initialValue]);

  const onChangeRef = React.useRef(onChange);
  React.useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const lastEmittedRef = React.useRef<string>(
    JSON.stringify({
      search: (initialValue ?? '').trim() === '' ? null : (initialValue ?? '').trim(),
    }),
  );

  React.useEffect(() => {
    const timer = setTimeout(() => {
      const payload = {
        search: (searchValue.trim() === '' ? null : searchValue.trim()),
      };
      const key = JSON.stringify(payload);
      if (lastEmittedRef.current !== key) {
        lastEmittedRef.current = key;
        onChangeRef.current(payload);
      }
    }, debounceMs);

    return () => clearTimeout(timer);
  }, [searchValue, debounceMs]);

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const payload = { search: toNull(searchValue) };
      const key = JSON.stringify(payload);
      if (lastEmittedRef.current !== key) {
        lastEmittedRef.current = key;
        onChangeRef.current(payload);
      }
    }
  };

  const onExport = async () => {
    try {
      setDownloading(true);
      const { data } = await api.downloadBurnCenters({
        search: searchValue || null,
      });
      fileDownload(data, 'BurnCenters.csv');
    } catch {
      // alert('Error exporting file');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Stack
      direction="horizontal"
      gap={2}
      className="flex-wrap justify-content-end align-items-center"
      style={{ minWidth: 0 }}
    >
      <FormControl
        placeholder="Search"
        value={searchValue}
        onChange={(e) => setSearchValue(e.target.value)}
        onKeyDown={handleKeyDown}
        style={{ flex: '1 1 240px', minWidth: '200px', maxWidth: '320px' }}
      />
      <Button variant="secondary" onClick={onExport} disabled={downloading} className="flex-shrink-0">
        {downloading ? <FaCog className="fa-spin" /> : 'Export'}
      </Button>
    </Stack>
  );
};

export default BurnCenterListFilter;
